import React from 'react';

import type { ProgressSnapshot } from '../api/learning';
import {
  LEARNING_PROFILE_LABEL,
  NOT_ENOUGH_HISTORY_COPY,
  OBSERVATION_LABEL,
  WORKING_VOCABULARY_NOTE,
  profileOf,
  missedClassesLine,
  progressHeadline,
  progressModeOf,
} from '../vocabulary/learningVocabulary';

/**
 * One enrollment's progress, rendered from the server's snapshot and nothing
 * else (PRT-09-FR-04/05). The parent dashboard and the progress screen both
 * use this, so the same enrollment reads the same way in both places.
 *
 * Mode-aware (MC01-R10): HISTORY_ONLY (LEP_0) shows classes attended;
 * SIMPLE_QUALITATIVE (LEP_1) adds the latest observation in its working
 * vocabulary; SERVICE_ONLY (a course with no profile yet) shows classes
 * given and nothing about learning. Milestone counts exist only for the
 * deferred LEP_2/3. What does not apply is not drawn — no zero, no "falta".
 * Not enough history reads "pendiente", never 0 %.
 */
const LearningProgressSummary: React.FC<{ snapshot: ProgressSnapshot; compact?: boolean }> = ({
  snapshot,
  compact,
}) => {
  const profile = profileOf(snapshot.learningProfile);
  const mode = progressModeOf(snapshot);
  const headline = progressHeadline(snapshot);
  const missed = mode === 'SERVICE_ONLY' ? null : missedClassesLine(snapshot);
  const structured = mode === 'STRUCTURED_OUTCOME' || mode === 'MASTERY_PROGRESSION';
  const showBar = structured && !!snapshot.milestones && headline !== null;
  const ratio =
    showBar && snapshot.milestones && snapshot.milestones.total > 0
      ? snapshot.milestones.completed / snapshot.milestones.total
      : 0;

  return (
    <div style={{ margin: '12px 0' }} data-testid={`progress-${snapshot.enrollmentId}`}>
      <div style={{ fontSize: 13, color: '#495057', marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>Avance</span>
        {!compact && <> · {LEARNING_PROFILE_LABEL[profile]}</>}
      </div>
      {headline === null ? (
        <div style={{ fontSize: 14, color: '#666' }}>{NOT_ENOUGH_HISTORY_COPY}</div>
      ) : (
        <div style={{ fontSize: 14 }}>{headline}</div>
      )}
      {missed && <div style={{ fontSize: 13, color: '#495057', marginTop: 4 }}>{missed}</div>}
      {showBar && (
        <div
          role="progressbar"
          aria-label="Hitos completados"
          aria-valuemin={0}
          aria-valuemax={snapshot.milestones?.total ?? 0}
          aria-valuenow={snapshot.milestones?.completed ?? 0}
          style={{
            height: 8,
            borderRadius: 4,
            background: '#e9ecef',
            overflow: 'hidden',
            marginTop: 6,
          }}
        >
          <div style={{ width: `${ratio * 100}%`, height: '100%', background: '#2b8a3e' }} />
        </div>
      )}
      {mode === 'SIMPLE_QUALITATIVE' && snapshot.latestObservation && (
        <div style={{ fontSize: 14, marginTop: 6 }}>
          Cómo lo vio el profesor en la sesión {snapshot.latestObservation.sequence}:{' '}
          <strong>{OBSERVATION_LABEL[snapshot.latestObservation.state]}</strong>
          {!compact && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              {WORKING_VOCABULARY_NOTE}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LearningProgressSummary;
